import type { UnixTimestamp } from "@note-app/contracts";
import {
  reportProgress,
  throwIfCancelled,
  type AuthoritativeDataSource,
  type AuthoritativeEntryKind,
  type BackupCodec,
  type EncodedBackup,
  type EncodedBackupSummary,
  type HashPort,
  type IdGenerator,
  type OperationContext,
  type StreamingBackupCodec,
} from "@note-app/knowledge-services";
import { nextGeneratedId } from "@note-app/knowledge-services";
import {
  assertJsonValue,
  assertUuid,
  migrateCanvasToCurrent,
  migrateDocumentToCurrent,
  migrateFolderToCurrent,
  migrateKnowledgeItemToCurrent,
  migrateRevisionRecordToCurrent,
  migrateTagToCurrent,
} from "@note-app/domain";

export const NOTEBUNDLE_FORMAT = "note-app.notebundle";
export const NOTEBUNDLE_VERSION = 1;
export const NOTEBUNDLE_PROFILE = "portable-authoritative";
const MANIFEST_PATH = "manifest.json";

export type ImportExportErrorCode =
  | "invalid-archive"
  | "invalid-manifest"
  | "unsupported-version"
  | "checksum-mismatch"
  | "unsafe-path"
  | "restore-conflict"
  | "restore-failed";

export class ImportExportError extends Error {
  constructor(
    readonly code: ImportExportErrorCode,
    message: string,
    readonly details: Readonly<Record<string, unknown>> = {},
    options?: ErrorOptions,
  ) {
    super(message, options);
    this.name = "ImportExportError";
  }
}

export interface BundleManifestEntry {
  path: string;
  kind: AuthoritativeEntryKind;
  mediaType: string;
  byteLength: number;
  sha256: string;
}

export interface NotebundleManifest {
  format: typeof NOTEBUNDLE_FORMAT;
  bundleVersion: typeof NOTEBUNDLE_VERSION;
  profile: typeof NOTEBUNDLE_PROFILE;
  schemaVersion: number;
  createdAt: UnixTimestamp;
  sourceKnowledgeBaseId: string;
  entries: readonly BundleManifestEntry[];
  projections: {
    included: false;
    rebuildAfterRestore: readonly [
      "search",
      "references",
      "attachment-reference-counts",
    ];
  };
}

export interface ArchiveEntry {
  path: string;
  bytes: Uint8Array;
}

/**
 * A platform archive adapter may use ZIP, tar, or another container. Entry
 * paths and bytes are the semantic contract; notebundle validation never trusts
 * the container's directory handling.
 */
export interface ArchivePort {
  pack(entries: readonly ArchiveEntry[]): Promise<Uint8Array>;
  unpack(archive: Uint8Array): Promise<readonly ArchiveEntry[]>;
}

export interface StreamingArchiveEntry {
  path: string;
  open(): AsyncIterable<Uint8Array>;
}

export interface StreamingArchivePort {
  packTo(
    entries: AsyncIterable<StreamingArchiveEntry>,
    write: (chunk: Uint8Array) => Promise<void>,
  ): Promise<void>;
  unpackTo(
    archive: AsyncIterable<Uint8Array>,
    consume: (
      entry: StreamingArchiveEntry,
    ) => Promise<void>,
  ): Promise<void>;
}

export interface ExportedNotebundle extends EncodedBackup {
  manifest: NotebundleManifest;
}

export interface ValidatedBundleEntry extends BundleManifestEntry {
  bytes: Uint8Array;
}

export interface ValidatedNotebundle {
  manifest: NotebundleManifest;
  entries: readonly ValidatedBundleEntry[];
  bundleSha256: string;
}

export interface RestorePlan {
  mode: "create-new";
  targetKnowledgeBaseId: string;
  sourceKnowledgeBaseId: string;
  bundleSha256: string;
  entryCount: number;
  actions: readonly RestoreAction[];
}

export type RestoreAction =
  | { kind: "create-knowledge-base"; knowledgeBaseId: string }
  | { kind: "write-authoritative-entry"; path: string }
  | {
      kind: "rebuild-projections";
      projections: readonly [
        "search",
        "references",
        "attachment-reference-counts",
      ];
    };

export interface RestoreCatalogPort {
  exists(knowledgeBaseId: string): Promise<boolean>;
}

export interface RestoreTargetPort {
  createKnowledgeBase(
    knowledgeBaseId: string,
    metadata: {
      restoredFromKnowledgeBaseId: string;
      restoredFromBundleSha256: string;
    },
  ): Promise<void>;
  writeAuthoritativeEntry(
    knowledgeBaseId: string,
    entry: ValidatedBundleEntry,
  ): Promise<void>;
  rebuildProjections(
    knowledgeBaseId: string,
    context?: OperationContext,
  ): Promise<{
    searchItems: number;
    referenceItems: number;
    attachments: number;
  }>;
  finalizeKnowledgeBase(knowledgeBaseId: string): Promise<void>;
  abandonKnowledgeBase(knowledgeBaseId: string, cause: unknown): Promise<void>;
}

export interface StreamingRestoreTargetPort {
  createStagingKnowledgeBase(knowledgeBaseId: string): Promise<void>;
  writeStagedEntry(
    knowledgeBaseId: string,
    entry: StreamingArchiveEntry,
  ): Promise<void>;
  prepareStagedKnowledgeBase(
    knowledgeBaseId: string,
    manifest: NotebundleManifest,
    metadata: {
      restoredFromBundleSha256: string;
    },
  ): Promise<void>;
  rebuildStagedProjections(
    knowledgeBaseId: string,
    context?: OperationContext,
  ): Promise<{
    searchItems: number;
    referenceItems: number;
    attachments: number;
  }>;
  publishStagedKnowledgeBase(knowledgeBaseId: string): Promise<void>;
  abandonStagedKnowledgeBase(
    knowledgeBaseId: string,
    cause: unknown,
  ): Promise<void>;
}

export interface RestoreReport {
  targetKnowledgeBaseId: string;
  sourceKnowledgeBaseId: string;
  bundleSha256: string;
  entryCount: number;
  byteLength: number;
  rebuilt: {
    searchItems: number;
    referenceItems: number;
    attachments: number;
  };
  published: true;
  warnings: readonly string[];
}

export class NotebundleService {
  constructor(
    private readonly archive: ArchivePort,
    private readonly hash: HashPort,
    private readonly ids: IdGenerator,
  ) {}

  async export(
    source: AuthoritativeDataSource,
    createdAt: UnixTimestamp,
    context?: OperationContext,
  ): Promise<ExportedNotebundle> {
    const sourceKnowledgeBaseId = await source.knowledgeBaseId();
    validateKnowledgeBaseId(sourceKnowledgeBaseId, "sourceKnowledgeBaseId");
    const archiveEntries: ArchiveEntry[] = [];
    const manifestEntries: BundleManifestEntry[] = [];
    const seen = new Set<string>();
    let completed = 0;
    for await (const entry of source.listAuthoritativeEntries(context)) {
      throwIfCancelled(context?.signal);
      validateEntryPath(entry.path);
      if (entry.path === MANIFEST_PATH || seen.has(entry.path)) {
        throw new ImportExportError(
          "invalid-manifest",
          `Duplicate or reserved bundle path: ${entry.path}`,
          { path: entry.path },
        );
      }
      seen.add(entry.path);
      const bytes = await collectBytes(entry.open(), context);
      const sha256 = await this.hash.sha256(bytes);
      archiveEntries.push({ path: entry.path, bytes });
      manifestEntries.push({
        path: entry.path,
        kind: entry.kind,
        mediaType: entry.mediaType,
        byteLength: bytes.byteLength,
        sha256,
      });
      completed += 1;
      reportProgress(context, {
        phase: "notebundle-export",
        completed,
      });
    }

    manifestEntries.sort((left, right) => left.path.localeCompare(right.path));
    archiveEntries.sort((left, right) => left.path.localeCompare(right.path));
    const manifest = createManifest(
      sourceKnowledgeBaseId,
      createdAt,
      manifestEntries,
    );
    const manifestBytes = encodeJson(manifest);
    const bytes = await this.archive.pack([
      { path: MANIFEST_PATH, bytes: manifestBytes },
      ...archiveEntries,
    ]);
    return {
      bytes,
      entryCount: manifestEntries.length,
      sha256: await this.hash.sha256(bytes),
      manifest,
    };
  }

  async validate(
    bytes: Uint8Array,
    context?: OperationContext,
  ): Promise<ValidatedNotebundle> {
    throwIfCancelled(context?.signal);
    let archiveEntries: readonly ArchiveEntry[];
    try {
      archiveEntries = await this.archive.unpack(bytes);
    } catch (error) {
      throw new ImportExportError(
        "invalid-archive",
        "The notebundle archive could not be read.",
        {},
        { cause: error },
      );
    }
    const byPath = new Map<string, Uint8Array>();
    for (const entry of archiveEntries) {
      validateEntryPath(entry.path);
      if (byPath.has(entry.path)) {
        throw new ImportExportError(
          "invalid-archive",
          `Archive contains duplicate path ${entry.path}.`,
        );
      }
      byPath.set(entry.path, entry.bytes);
    }
    const manifestBytes = byPath.get(MANIFEST_PATH);
    if (!manifestBytes) {
      throw new ImportExportError(
        "invalid-manifest",
        "The notebundle has no manifest.json.",
      );
    }
    const manifest = parseManifest(manifestBytes);
    const expectedPaths = new Set<string>([MANIFEST_PATH]);
    const validatedEntries: ValidatedBundleEntry[] = [];
    let completed = 0;
    for (const entry of manifest.entries) {
      throwIfCancelled(context?.signal);
      validateEntryPath(entry.path);
      if (expectedPaths.has(entry.path)) {
        throw new ImportExportError(
          "invalid-manifest",
          `Manifest contains duplicate or reserved path ${entry.path}.`,
        );
      }
      expectedPaths.add(entry.path);
      const payload = byPath.get(entry.path);
      if (!payload) {
        throw new ImportExportError(
          "invalid-archive",
          `Archive is missing ${entry.path}.`,
        );
      }
      if (payload.byteLength !== entry.byteLength) {
        throw new ImportExportError(
          "checksum-mismatch",
          `Length check failed for ${entry.path}.`,
          {
            path: entry.path,
            expected: entry.byteLength,
            actual: payload.byteLength,
          },
        );
      }
      const actualSha256 = await this.hash.sha256(payload);
      if (actualSha256.toLowerCase() !== entry.sha256.toLowerCase()) {
        throw new ImportExportError(
          "checksum-mismatch",
          `Checksum failed for ${entry.path}.`,
          { path: entry.path, expected: entry.sha256, actual: actualSha256 },
        );
      }
      validatedEntries.push({ ...entry, bytes: payload.slice() });
      completed += 1;
      reportProgress(context, {
        phase: "notebundle-validate",
        completed,
        total: manifest.entries.length,
      });
    }
    const unexpected = [...byPath.keys()].filter(
      (path) => !expectedPaths.has(path),
    );
    if (unexpected.length > 0) {
      throw new ImportExportError(
        "invalid-archive",
        "Archive contains entries not declared by the manifest.",
        { unexpected },
      );
    }
    validateAuthoritativeEntries(validatedEntries);
    return {
      manifest,
      entries: validatedEntries,
      bundleSha256: await this.hash.sha256(bytes),
    };
  }

  async planRestore(
    bundle: ValidatedNotebundle,
    catalog: RestoreCatalogPort,
    requestedTargetKnowledgeBaseId?: string,
  ): Promise<RestorePlan> {
    const targetKnowledgeBaseId =
      requestedTargetKnowledgeBaseId ?? nextGeneratedId(this.ids);
    validateKnowledgeBaseId(
      targetKnowledgeBaseId,
      "targetKnowledgeBaseId",
    );
    if (await catalog.exists(targetKnowledgeBaseId)) {
      throw new ImportExportError(
        "restore-conflict",
        `Knowledge base ${targetKnowledgeBaseId} already exists; overwrite is forbidden.`,
        { targetKnowledgeBaseId },
      );
    }
    return {
      mode: "create-new",
      targetKnowledgeBaseId,
      sourceKnowledgeBaseId: bundle.manifest.sourceKnowledgeBaseId,
      bundleSha256: bundle.bundleSha256,
      entryCount: bundle.entries.length,
      actions: [
        { kind: "create-knowledge-base", knowledgeBaseId: targetKnowledgeBaseId },
        ...bundle.entries.map(
          (entry): RestoreAction => ({
            kind: "write-authoritative-entry",
            path: entry.path,
          }),
        ),
        {
          kind: "rebuild-projections",
          projections: [
            "search",
            "references",
            "attachment-reference-counts",
          ],
        },
      ],
    };
  }

  async restore(
    bundle: ValidatedNotebundle,
    plan: RestorePlan,
    target: RestoreTargetPort,
    context?: OperationContext,
  ): Promise<void> {
    if (
      plan.mode !== "create-new" ||
      plan.bundleSha256 !== bundle.bundleSha256 ||
      plan.entryCount !== bundle.entries.length ||
      plan.sourceKnowledgeBaseId !== bundle.manifest.sourceKnowledgeBaseId ||
      plan.targetKnowledgeBaseId.trim() === ""
    ) {
      throw new ImportExportError(
        "restore-conflict",
        "Restore plan does not match the validated bundle.",
      );
    }
    let created = false;
    try {
      await target.createKnowledgeBase(plan.targetKnowledgeBaseId, {
        restoredFromKnowledgeBaseId: plan.sourceKnowledgeBaseId,
        restoredFromBundleSha256: plan.bundleSha256,
      });
      created = true;
      for (const [index, entry] of bundle.entries.entries()) {
        throwIfCancelled(context?.signal);
        await target.writeAuthoritativeEntry(
          plan.targetKnowledgeBaseId,
          entry,
        );
        reportProgress(context, {
          phase: "notebundle-restore",
          completed: index + 1,
          total: bundle.entries.length,
        });
      }
      throwIfCancelled(context?.signal);
      await target.rebuildProjections(
        plan.targetKnowledgeBaseId,
        context,
      );
      throwIfCancelled(context?.signal);
      await target.finalizeKnowledgeBase(plan.targetKnowledgeBaseId);
    } catch (error) {
      let failure: unknown = error;
      if (created) {
        try {
          await target.abandonKnowledgeBase(
            plan.targetKnowledgeBaseId,
            error,
          );
        } catch (abandonError) {
          failure = new AggregateError(
            [error, abandonError],
            "Restore and staging cleanup both failed.",
          );
        }
      }
      if (failure === error && error instanceof ImportExportError) throw error;
      throw new ImportExportError(
        "restore-failed",
        created
          ? "Restore failed; the incomplete new knowledge base was abandoned or reported a cleanup failure."
          : "Restore failed before a new knowledge base was created.",
        { targetKnowledgeBaseId: plan.targetKnowledgeBaseId },
        { cause: failure },
      );
    }
  }

  async exportTo(
    source: AuthoritativeDataSource,
    createdAt: UnixTimestamp,
    archive: StreamingArchivePort,
    write: (chunk: Uint8Array) => Promise<void>,
    context?: OperationContext,
  ): Promise<EncodedBackupSummary & { manifest: NotebundleManifest }> {
    const sourceKnowledgeBaseId = await source.knowledgeBaseId();
    validateKnowledgeBaseId(sourceKnowledgeBaseId, "sourceKnowledgeBaseId");
    const bundleHasher = this.hash.createSha256();
    const manifestEntries: BundleManifestEntry[] = [];
    const seen = new Set<string>();
    let byteLength = 0;
    let manifest: NotebundleManifest | undefined;
    const entries = async function* (
      service: NotebundleService,
    ): AsyncIterable<StreamingArchiveEntry> {
      for await (const entry of source.listAuthoritativeEntries(context)) {
        throwIfCancelled(context?.signal);
        validateEntryPath(entry.path);
        if (entry.path === MANIFEST_PATH || seen.has(entry.path)) {
          throw new ImportExportError(
            "invalid-manifest",
            `Duplicate or reserved bundle path: ${entry.path}`,
            { path: entry.path },
          );
        }
        seen.add(entry.path);
        const hasher = service.hash.createSha256();
        let entryLength = 0;
        yield {
          path: entry.path,
          async *open() {
            for await (const chunk of entry.open()) {
              throwIfCancelled(context?.signal);
              hasher.update(chunk);
              entryLength += chunk.byteLength;
              yield chunk;
            }
          },
        };
        manifestEntries.push({
          path: entry.path,
          kind: entry.kind,
          mediaType: entry.mediaType,
          byteLength: entryLength,
          sha256: hasher.digestHex().toLowerCase(),
        });
        reportProgress(context, {
          phase: "notebundle-export",
          completed: manifestEntries.length,
        });
      }
      manifestEntries.sort((left, right) => left.path.localeCompare(right.path));
      manifest = createManifest(
        sourceKnowledgeBaseId,
        createdAt,
        manifestEntries,
      );
      const manifestBytes = encodeJson(manifest);
      yield {
        path: MANIFEST_PATH,
        async *open() {
          yield manifestBytes;
        },
      };
    };
    await archive.packTo(entries(this), async (chunk) => {
      throwIfCancelled(context?.signal);
      bundleHasher.update(chunk);
      byteLength += chunk.byteLength;
      await write(chunk);
    });
    if (!manifest) throw new Error("Streaming archive did not consume entries");
    return {
      byteLength,
      entryCount: manifestEntries.length,
      sha256: bundleHasher.digestHex().toLowerCase(),
      manifest,
    };
  }

  /**
   * Restores directly into a hidden staging knowledge base. Archive entries
   * are hashed while streaming to the target; publication occurs only after
   * manifest validation and all three projection rebuilds succeed.
   */
  async restoreFromStream(
    source: AsyncIterable<Uint8Array>,
    archive: StreamingArchivePort,
    catalog: RestoreCatalogPort,
    target: StreamingRestoreTargetPort,
    requestedTargetKnowledgeBaseId?: string,
    context?: OperationContext,
  ): Promise<RestoreReport> {
    const targetKnowledgeBaseId =
      requestedTargetKnowledgeBaseId ?? nextGeneratedId(this.ids);
    validateKnowledgeBaseId(
      targetKnowledgeBaseId,
      "targetKnowledgeBaseId",
    );
    if (await catalog.exists(targetKnowledgeBaseId)) {
      throw new ImportExportError(
        "restore-conflict",
        `Knowledge base ${targetKnowledgeBaseId} already exists.`,
      );
    }
    const bundleHasher = this.hash.createSha256();
    const actual = new Map<
      string,
      { byteLength: number; sha256: string }
    >();
    const seenPaths = new Set<string>();
    let manifestBytes: Uint8Array | undefined;
    let bundleLength = 0;
    let stagingCreated = false;
    const hashedSource = hashStream(
      source,
      bundleHasher,
      (length) => {
        bundleLength += length;
      },
      context,
    );
    try {
      await target.createStagingKnowledgeBase(targetKnowledgeBaseId);
      stagingCreated = true;
      await archive.unpackTo(hashedSource, async (entry) => {
        throwIfCancelled(context?.signal);
        validateEntryPath(entry.path);
        if (seenPaths.has(entry.path)) {
          throw new ImportExportError(
            "invalid-archive",
            `Archive contains duplicate path ${entry.path}.`,
          );
        }
        seenPaths.add(entry.path);
        if (entry.path === MANIFEST_PATH) {
          manifestBytes = await collectSmallBytes(
            entry.open(),
            4 * 1024 * 1024,
            context,
          );
          return;
        }
        const hasher = this.hash.createSha256();
        let length = 0;
        await target.writeStagedEntry(targetKnowledgeBaseId, {
          path: entry.path,
          open: () =>
            hashStream(
              entry.open(),
              hasher,
              (size) => {
                length += size;
              },
              context,
            ),
        });
        actual.set(entry.path, {
          byteLength: length,
          sha256: hasher.digestHex().toLowerCase(),
        });
        reportProgress(context, {
          phase: "notebundle-restore-stage",
          completed: actual.size,
        });
      });
      if (!manifestBytes) {
        throw new ImportExportError(
          "invalid-manifest",
          "The notebundle has no manifest.json.",
        );
      }
      const manifest = parseManifest(manifestBytes);
      validateStreamedEntries(manifest, actual);
      validateManifestCompleteness(manifest);
      const bundleSha256 = bundleHasher.digestHex().toLowerCase();
      await target.prepareStagedKnowledgeBase(
        targetKnowledgeBaseId,
        manifest,
        { restoredFromBundleSha256: bundleSha256 },
      );
      throwIfCancelled(context?.signal);
      const rebuilt = await target.rebuildStagedProjections(
        targetKnowledgeBaseId,
        context,
      );
      throwIfCancelled(context?.signal);
      await target.publishStagedKnowledgeBase(targetKnowledgeBaseId);
      return {
        targetKnowledgeBaseId,
        sourceKnowledgeBaseId: manifest.sourceKnowledgeBaseId,
        bundleSha256,
        entryCount: manifest.entries.length,
        byteLength: bundleLength,
        rebuilt,
        published: true,
        warnings: [],
      };
    } catch (error) {
      let failure: unknown = error;
      if (stagingCreated) {
        try {
          await target.abandonStagedKnowledgeBase(
            targetKnowledgeBaseId,
            error,
          );
        } catch (abandonError) {
          failure = new AggregateError(
            [error, abandonError],
            "Streaming restore and staging cleanup both failed.",
          );
        }
      }
      if (failure === error && error instanceof ImportExportError) throw error;
      throw new ImportExportError(
        "restore-failed",
        stagingCreated
          ? "Streaming restore failed; the hidden staging knowledge base was abandoned or reported a cleanup failure."
          : "Streaming restore failed before staging was created.",
        { targetKnowledgeBaseId },
        { cause: failure },
      );
    }
  }
}

export class NotebundleBackupCodec
  implements BackupCodec, StreamingBackupCodec
{
  constructor(
    private readonly service: NotebundleService,
    private readonly streamingArchive?: StreamingArchivePort,
  ) {}

  encode(
    source: AuthoritativeDataSource,
    createdAt: UnixTimestamp,
    context?: OperationContext,
  ): Promise<EncodedBackup> {
    return this.service.export(source, createdAt, context);
  }

  encodeTo(
    source: AuthoritativeDataSource,
    createdAt: UnixTimestamp,
    write: (chunk: Uint8Array) => Promise<void>,
    context?: OperationContext,
  ): Promise<EncodedBackupSummary> {
    if (!this.streamingArchive) {
      throw new ImportExportError(
        "restore-failed",
        "A StreamingArchivePort is required for streaming backup.",
      );
    }
    return this.service.exportTo(
      source,
      createdAt,
      this.streamingArchive,
      write,
      context,
    );
  }
}

function parseManifest(bytes: Uint8Array): NotebundleManifest {
  let value: unknown;
  try {
    value = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(bytes));
  } catch (error) {
    throw new ImportExportError(
      "invalid-manifest",
      "manifest.json is not valid UTF-8 JSON.",
      {},
      { cause: error },
    );
  }
  if (!isRecord(value) || value["format"] !== NOTEBUNDLE_FORMAT) {
    throw new ImportExportError(
      "invalid-manifest",
      "manifest.json has the wrong format identifier.",
    );
  }
  if (value["bundleVersion"] !== NOTEBUNDLE_VERSION) {
    throw new ImportExportError(
      "unsupported-version",
      `Unsupported notebundle version ${String(value["bundleVersion"])}.`,
    );
  }
  if (
    value["profile"] !== undefined &&
    value["profile"] !== NOTEBUNDLE_PROFILE
  ) {
    throw new ImportExportError(
      "unsupported-version",
      `Unsupported notebundle profile ${String(value["profile"])}. This importer accepts portable authoritative backups only.`,
    );
  }
  if (
    typeof value["schemaVersion"] === "number" &&
    Number.isSafeInteger(value["schemaVersion"]) &&
    value["schemaVersion"] > 1
  ) {
    throw new ImportExportError(
      "unsupported-version",
      `Notebundle schema ${value["schemaVersion"]} is newer than the supported schema 1; restore was not started.`,
    );
  }
  if (
    value["schemaVersion"] !== 1 ||
    typeof value["createdAt"] !== "number" ||
    !Number.isSafeInteger(value["createdAt"]) ||
    value["createdAt"] < 0 ||
    typeof value["sourceKnowledgeBaseId"] !== "string" ||
    !Array.isArray(value["entries"])
  ) {
    throw new ImportExportError(
      "invalid-manifest",
      "manifest.json is missing required fields.",
    );
  }
  validateKnowledgeBaseId(
    value["sourceKnowledgeBaseId"],
    "sourceKnowledgeBaseId",
  );
  const projections = value["projections"];
  if (
    !isRecord(projections) ||
    projections["included"] !== false
  ) {
    throw new ImportExportError(
      "invalid-manifest",
      "Manifest must exclude rebuildable projections.",
    );
  }
  const rebuildAfterRestore = projections["rebuildAfterRestore"];
  if (
    !Array.isArray(rebuildAfterRestore) ||
    !["search", "references", "attachment-reference-counts"].every(
      (projection) => rebuildAfterRestore.includes(projection),
    )
  ) {
    throw new ImportExportError(
      "invalid-manifest",
      "Manifest must declare every projection rebuild.",
    );
  }
  const entries = value["entries"].map(parseManifestEntry);
  const manifest: NotebundleManifest = {
    format: NOTEBUNDLE_FORMAT,
    bundleVersion: NOTEBUNDLE_VERSION,
    profile: NOTEBUNDLE_PROFILE,
    schemaVersion: value["schemaVersion"],
    createdAt: value["createdAt"],
    sourceKnowledgeBaseId: value["sourceKnowledgeBaseId"],
    entries,
    projections: {
      included: false,
      rebuildAfterRestore: [
        "search",
        "references",
        "attachment-reference-counts",
      ],
    },
  };
  validateManifestCompleteness(manifest);
  return manifest;
}

const ENTRY_KINDS = new Set<AuthoritativeEntryKind>([
  "knowledge-item",
  "document",
  "canvas",
  "folder",
  "tag",
  "revision",
  "attachment-metadata",
  "attachment-blob",
  "settings",
]);

function parseManifestEntry(value: unknown): BundleManifestEntry {
  if (
    !isRecord(value) ||
    typeof value["path"] !== "string" ||
    typeof value["kind"] !== "string" ||
    !ENTRY_KINDS.has(value["kind"] as AuthoritativeEntryKind) ||
    typeof value["mediaType"] !== "string" ||
    typeof value["byteLength"] !== "number" ||
    !Number.isSafeInteger(value["byteLength"]) ||
    value["byteLength"] < 0 ||
    typeof value["sha256"] !== "string" ||
    !/^[a-fA-F0-9]{64}$/u.test(value["sha256"])
  ) {
    throw new ImportExportError(
      "invalid-manifest",
      "Manifest contains an invalid entry.",
    );
  }
  return {
    path: value["path"],
    kind: value["kind"] as AuthoritativeEntryKind,
    mediaType: value["mediaType"],
    byteLength: value["byteLength"],
    sha256: value["sha256"].toLowerCase(),
  };
}

function validateEntryPath(entryPath: string): void {
  if (
    entryPath === "" ||
    entryPath.startsWith("/") ||
    entryPath.includes("\\") ||
    entryPath.split("/").some((part) => part === "" || part === "." || part === "..")
  ) {
    throw new ImportExportError(
      "unsafe-path",
      `Unsafe archive entry path: ${entryPath}`,
      { path: entryPath },
    );
  }
}

async function collectBytes(
  stream: AsyncIterable<Uint8Array>,
  context?: OperationContext,
): Promise<Uint8Array> {
  const chunks: Uint8Array[] = [];
  let length = 0;
  for await (const chunk of stream) {
    throwIfCancelled(context?.signal);
    chunks.push(chunk.slice());
    length += chunk.byteLength;
  }
  const bytes = new Uint8Array(length);
  let offset = 0;
  for (const chunk of chunks) {
    bytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return bytes;
}

function encodeJson(value: unknown): Uint8Array {
  return new TextEncoder().encode(JSON.stringify(value));
}

function createManifest(
  sourceKnowledgeBaseId: string,
  createdAt: UnixTimestamp,
  entries: readonly BundleManifestEntry[],
): NotebundleManifest {
  if (!Number.isSafeInteger(createdAt) || createdAt < 0) {
    throw new ImportExportError(
      "invalid-manifest",
      "createdAt must be a non-negative Unix timestamp.",
    );
  }
  return {
    format: NOTEBUNDLE_FORMAT,
    bundleVersion: NOTEBUNDLE_VERSION,
    profile: NOTEBUNDLE_PROFILE,
    schemaVersion: NOTEBUNDLE_VERSION,
    createdAt,
    sourceKnowledgeBaseId,
    entries,
    projections: {
      included: false,
      rebuildAfterRestore: [
        "search",
        "references",
        "attachment-reference-counts",
      ],
    },
  };
}

function validateKnowledgeBaseId(value: string, path: string): void {
  if (
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(
      value,
    )
  ) {
    throw new ImportExportError(
      "invalid-manifest",
      `${path} must be an RFC 4122 UUID.`,
      { path, value },
    );
  }
}

function validateManifestCompleteness(manifest: NotebundleManifest): void {
  const paths = new Set<string>();
  for (const entry of manifest.entries) {
    validateEntryPath(entry.path);
    if (entry.path === MANIFEST_PATH || paths.has(entry.path)) {
      throw new ImportExportError(
        "invalid-manifest",
        `Manifest contains duplicate or reserved path ${entry.path}.`,
      );
    }
    paths.add(entry.path);
  }
}

function validateStreamedEntries(
  manifest: NotebundleManifest,
  actual: ReadonlyMap<string, { byteLength: number; sha256: string }>,
): void {
  const declared = new Set<string>();
  for (const entry of manifest.entries) {
    declared.add(entry.path);
    const value = actual.get(entry.path);
    if (!value) {
      throw new ImportExportError(
        "invalid-archive",
        `Archive is missing ${entry.path}.`,
      );
    }
    if (
      value.byteLength !== entry.byteLength ||
      value.sha256 !== entry.sha256.toLowerCase()
    ) {
      throw new ImportExportError(
        "checksum-mismatch",
        `Checksum or length check failed for ${entry.path}.`,
        {
          path: entry.path,
          expectedLength: entry.byteLength,
          actualLength: value.byteLength,
          expectedSha256: entry.sha256,
          actualSha256: value.sha256,
        },
      );
    }
  }
  const unexpected = [...actual.keys()].filter((path) => !declared.has(path));
  if (unexpected.length > 0) {
    throw new ImportExportError(
      "invalid-archive",
      "Archive contains entries not declared by the manifest.",
      { unexpected },
    );
  }
}

async function* hashStream(
  source: AsyncIterable<Uint8Array>,
  hasher: { update(chunk: Uint8Array): void },
  addLength: (length: number) => void,
  context?: OperationContext,
): AsyncIterable<Uint8Array> {
  for await (const chunk of source) {
    throwIfCancelled(context?.signal);
    hasher.update(chunk);
    addLength(chunk.byteLength);
    yield chunk;
  }
}

async function collectSmallBytes(
  source: AsyncIterable<Uint8Array>,
  maximum: number,
  context?: OperationContext,
): Promise<Uint8Array> {
  const chunks: Uint8Array[] = [];
  let length = 0;
  for await (const chunk of source) {
    throwIfCancelled(context?.signal);
    length += chunk.byteLength;
    if (length > maximum) {
      throw new ImportExportError(
        "invalid-manifest",
        "manifest.json exceeds the safe size limit.",
      );
    }
    chunks.push(chunk.slice());
  }
  const result = new Uint8Array(length);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return result;
}

function validateAuthoritativeEntries(
  entries: readonly ValidatedBundleEntry[],
): void {
  const attachmentMetadata: Array<{
    attachmentId: string;
    sha256: string;
    storageKey: string;
    byteLength: number;
  }> = [];
  const blobHashes = new Set(
    entries
      .filter((entry) => entry.kind === "attachment-blob")
      .map((entry) => entry.sha256.toLowerCase()),
  );
  for (const entry of entries) {
    if (
      entry.kind === "attachment-blob" ||
      !entry.mediaType.toLowerCase().includes("json")
    ) {
      continue;
    }
    let value: unknown;
    try {
      value = JSON.parse(
        new TextDecoder("utf-8", { fatal: true }).decode(entry.bytes),
      );
    } catch (error) {
      throw new ImportExportError(
        "invalid-manifest",
        `${entry.path} is not valid UTF-8 JSON.`,
        { path: entry.path },
        { cause: error },
      );
    }
    try {
      switch (entry.kind) {
        case "knowledge-item":
          migrateKnowledgeItemToCurrent(
            value as Parameters<typeof migrateKnowledgeItemToCurrent>[0],
          );
          break;
        case "document":
          migrateDocumentToCurrent(
            value as Parameters<typeof migrateDocumentToCurrent>[0],
          );
          break;
        case "canvas":
          migrateCanvasToCurrent(
            value as Parameters<typeof migrateCanvasToCurrent>[0],
          );
          break;
        case "folder":
          migrateFolderToCurrent(
            value as Parameters<typeof migrateFolderToCurrent>[0],
          );
          break;
        case "tag":
          migrateTagToCurrent(
            value as Parameters<typeof migrateTagToCurrent>[0],
          );
          break;
        case "revision":
          migrateRevisionRecordToCurrent(
            value as Parameters<typeof migrateRevisionRecordToCurrent>[0],
          );
          break;
        case "settings":
          assertJsonValue(value, entry.path);
          break;
        case "attachment-metadata": {
          const metadata = parseAttachmentMetadata(value, entry.path);
          attachmentMetadata.push(metadata);
          break;
        }
      }
    } catch (error) {
      if (error instanceof ImportExportError) throw error;
      throw new ImportExportError(
        "invalid-manifest",
        `${entry.path} violates the current domain schema.`,
        { path: entry.path },
        { cause: error },
      );
    }
  }
  for (const metadata of attachmentMetadata) {
    if (!blobHashes.has(metadata.sha256)) {
      throw new ImportExportError(
        "invalid-manifest",
        `Attachment ${metadata.attachmentId} has no matching blob.`,
        {
          attachmentId: metadata.attachmentId,
          sha256: metadata.sha256,
        },
      );
    }
  }
}

function parseAttachmentMetadata(
  value: unknown,
  entryPath: string,
): {
  attachmentId: string;
  sha256: string;
  storageKey: string;
  byteLength: number;
} {
  if (!isRecord(value)) {
    throw new ImportExportError(
      "invalid-manifest",
      `${entryPath} must contain an attachment metadata object.`,
    );
  }
  const attachmentId =
    typeof value["attachmentId"] === "string"
      ? value["attachmentId"]
      : value["id"];
  const sha256 = value["sha256"];
  const storageKey = value["storageKey"];
  const byteLength = value["byteLength"];
  if (
    typeof attachmentId !== "string" ||
    typeof sha256 !== "string" ||
    !/^[a-f0-9]{64}$/u.test(sha256) ||
    typeof storageKey !== "string" ||
    storageKey !== sha256 ||
    typeof byteLength !== "number" ||
    !Number.isSafeInteger(byteLength) ||
    byteLength < 0
  ) {
    throw new ImportExportError(
      "invalid-manifest",
      `${entryPath} has invalid attachment identity, hash, storageKey or length.`,
    );
  }
  assertUuid(attachmentId, `${entryPath}.attachmentId`);
  return { attachmentId, sha256, storageKey, byteLength };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

interface PortableArchivePayload {
  format: "note-app.portable-json-archive";
  version: 1;
  entries: Array<{ path: string; base64: string }>;
}

/**
 * Dependency-free reference archive. Production platforms may inject ZIP;
 * this deterministic JSON container keeps tests and headless tooling runnable.
 */
export class PortableJsonArchiveAdapter implements ArchivePort {
  async pack(entries: readonly ArchiveEntry[]): Promise<Uint8Array> {
    const payload: PortableArchivePayload = {
      format: "note-app.portable-json-archive",
      version: 1,
      entries: entries.map((entry) => ({
        path: entry.path,
        base64: encodeBase64(entry.bytes),
      })),
    };
    return encodeJson(payload);
  }

  async unpack(archive: Uint8Array): Promise<readonly ArchiveEntry[]> {
    const parsed: unknown = JSON.parse(
      new TextDecoder("utf-8", { fatal: true }).decode(archive),
    );
    if (
      !isRecord(parsed) ||
      parsed["format"] !== "note-app.portable-json-archive" ||
      parsed["version"] !== 1 ||
      !Array.isArray(parsed["entries"])
    ) {
      throw new Error("Invalid portable JSON archive.");
    }
    return parsed["entries"].map((value) => {
      if (
        !isRecord(value) ||
        typeof value["path"] !== "string" ||
        typeof value["base64"] !== "string"
      ) {
        throw new Error("Invalid portable JSON archive entry.");
      }
      return { path: value["path"], bytes: decodeBase64(value["base64"]) };
    });
  }
}

const BASE64_ALPHABET =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";

function encodeBase64(bytes: Uint8Array): string {
  let result = "";
  for (let index = 0; index < bytes.length; index += 3) {
    const first = bytes[index] ?? 0;
    const second = bytes[index + 1] ?? 0;
    const third = bytes[index + 2] ?? 0;
    const value = (first << 16) | (second << 8) | third;
    result += BASE64_ALPHABET[(value >> 18) & 63];
    result += BASE64_ALPHABET[(value >> 12) & 63];
    result += index + 1 < bytes.length ? BASE64_ALPHABET[(value >> 6) & 63] : "=";
    result += index + 2 < bytes.length ? BASE64_ALPHABET[value & 63] : "=";
  }
  return result;
}

function decodeBase64(value: string): Uint8Array {
  if (value.length % 4 !== 0 || /[^A-Za-z0-9+/=]/u.test(value)) {
    throw new Error("Invalid base64 payload.");
  }
  const padding = value.endsWith("==") ? 2 : value.endsWith("=") ? 1 : 0;
  const bytes = new Uint8Array((value.length / 4) * 3 - padding);
  let output = 0;
  for (let index = 0; index < value.length; index += 4) {
    const parts = [0, 1, 2, 3].map((offset) => {
      const character = value[index + offset];
      return character === "=" ? 0 : BASE64_ALPHABET.indexOf(character ?? "");
    });
    if (parts.some((part) => part < 0)) throw new Error("Invalid base64 payload.");
    const combined =
      ((parts[0] ?? 0) << 18) |
      ((parts[1] ?? 0) << 12) |
      ((parts[2] ?? 0) << 6) |
      (parts[3] ?? 0);
    if (output < bytes.length) bytes[output++] = (combined >> 16) & 255;
    if (output < bytes.length) bytes[output++] = (combined >> 8) & 255;
    if (output < bytes.length) bytes[output++] = combined & 255;
  }
  return bytes;
}
